import { announce } from "../lib/announce.ts";
import { cartChange, fetchCartSection } from "../lib/cart-api.ts";
import { Component } from "../lib/component.ts";
import { parseLineItemProperties } from "../lib/line-item-properties.ts";

interface ModalPanelLike extends HTMLElement {
  open: () => void;
  close: () => void;
  title: string;
}

function isModalPanelLike(el: Element | null): el is ModalPanelLike {
  if (!(el instanceof HTMLElement)) return false;
  const open: unknown = Reflect.get(el, "open");
  const close: unknown = Reflect.get(el, "close");
  return typeof open === "function" && typeof close === "function";
}

interface LineItemContext {
  key: string;
  quantity: number;
  properties: Record<string, string>;
}

class CartNote extends Component {
  #modal: ModalPanelLike | null = null;

  #context: LineItemContext = { key: "", quantity: 0, properties: {} };

  get #noteKey(): string {
    return this.dataset.noteKey ?? "Personal note";
  }

  connectedCallback(): void {
    super.connectedCallback();
    const modalEl = this.querySelector("modal-panel");
    this.#modal = isModalPanelLike(modalEl) ? modalEl : null;
    document.addEventListener("click", this.#handleClick, {
      signal: this.signal,
    });
    this.addEventListener("submit", this.#handleSubmit, {
      signal: this.signal,
    });
    this.addEventListener("input", this.#handleInput, {
      signal: this.signal,
    });
  }

  #handleInput = (): void => {
    const form = this.#modal?.querySelector("form");
    const errorEl = form?.querySelector('[role="alert"]');
    const textarea = form?.querySelector("textarea");
    const submitButton = this.#modal?.querySelector('[type="submit"]');
    if (
      !(textarea instanceof HTMLTextAreaElement) ||
      !(submitButton instanceof HTMLButtonElement)
    ) {
      return;
    }

    if (errorEl instanceof HTMLParagraphElement) errorEl.hidden = true;
    textarea.removeAttribute("aria-invalid");

    const { selectionStart, selectionEnd } = textarea;
    const normalized = textarea.value.replace(/\n{3,}/g, "\n\n");

    if (textarea.value !== normalized) {
      textarea.value = normalized;
      textarea.setSelectionRange(
        Math.min(selectionStart, normalized.length),
        Math.min(selectionEnd, normalized.length),
      );
    }

    submitButton.disabled = textarea.value.trim().length === 0;
  };

  #handleClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-action="edit-note"], [data-action="remove-note"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const context = this.#parseLineItemContext(button);
    if (!context) return;

    if (button.dataset.action === "edit-note") {
      this.#openEditor(context);
    } else {
      this.#removeNote(button, context);
    }
  };

  #openEditor(context: LineItemContext): void {
    this.#context = context;
    this.#modal?.open();

    const errorEl = this.#modal?.querySelector('[role="alert"]');
    if (errorEl instanceof HTMLParagraphElement) errorEl.hidden = true;

    const noteKey = this.#noteKey;
    const hasNote = noteKey in this.#context.properties;

    const textarea = this.#modal?.querySelector("textarea");
    if (textarea instanceof HTMLTextAreaElement) {
      textarea.value = this.#context.properties[noteKey] ?? "";
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
    }

    const submitButton = this.#modal?.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.textContent = hasNote
        ? (this.dataset.editText ?? "")
        : (this.dataset.addText ?? "");
    }
  }

  #removeNote(button: HTMLButtonElement, context: LineItemContext): void {
    const { key, quantity, properties } = context;
    properties[this.#noteKey] = "";

    const originalText = button.textContent ?? "";
    button.disabled = true;
    if (button.dataset.loadingText) {
      button.textContent = button.dataset.loadingText;
    }

    this.#updateCart({ key, quantity, properties }, this.dataset.announcementRemoved).catch(() => {
      button.disabled = false;
      button.textContent = originalText;
      if (this.dataset.announcementError) announce(this.dataset.announcementError);
    });
  }

  #handleSubmit = (event: SubmitEvent): void => {
    event.preventDefault();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const textarea = form.querySelector("textarea");
    const submitButton = form.querySelector('[type="submit"]');
    if (!(textarea instanceof HTMLTextAreaElement) || !this.#context.key) return;

    const note = textarea.value.trim();
    if (!note) {
      textarea.setAttribute("aria-invalid", "true");
      textarea.focus();
      const errorEl = form.querySelector('[role="alert"]');
      if (errorEl instanceof HTMLParagraphElement) {
        errorEl.textContent = this.dataset.errorRequired ?? "";
        errorEl.hidden = false;
      }
      return;
    }

    const properties = { ...this.#context.properties };

    properties[this.#noteKey] = note;

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      submitButton.dataset.originalText = submitButton.textContent ?? "";
      if (this.dataset.savingText) {
        submitButton.textContent = this.dataset.savingText;
      }
    }

    const { key, quantity } = this.#context;

    this.#updateCart({ key, quantity, properties }, this.dataset.announcementSaved)
      .then(() => {
        this.#modal?.close();
      })
      .catch(() => {
        if (this.dataset.announcementError) announce(this.dataset.announcementError);
      })
      .finally(() => {
        if (submitButton instanceof HTMLButtonElement) {
          submitButton.disabled = false;
          if (submitButton.dataset.originalText) {
            submitButton.textContent = submitButton.dataset.originalText;
          }
          delete submitButton.dataset.originalText;
        }
      });
  };

  #parseLineItemContext(button: HTMLButtonElement): LineItemContext | null {
    const key = button.dataset.lineItemKey ?? "";
    const quantity = Number(button.dataset.quantity);
    if (!key || !(quantity > 0)) return null;

    let rawProps: unknown;
    try {
      rawProps = JSON.parse(
        button.closest("[data-properties]")?.getAttribute("data-properties") ?? "[]",
      );
    } catch {
      return null;
    }

    const properties = parseLineItemProperties(rawProps);
    if (!properties) return null;

    return { key, quantity, properties };
  }

  #updateCart(
    { key, quantity, properties }: LineItemContext,
    announcement: string | undefined,
  ): Promise<void> {
    return cartChange({ id: key, quantity, properties })
      .then(() => fetchCartSection("cart-drawer"))
      .then((html) => {
        document.dispatchEvent(new CustomEvent("cart:render", { detail: { html, announcement } }));
      });
  }
}

if (!customElements.get("cart-note")) {
  customElements.define("cart-note", CartNote);
}
